import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import Image from "next/image";
import Link from "next/link";

import FussyPointLabel from "~/components/quest/FussyPointLabel";
import UserQuestProgressStatusLabel from "~/components/quest/UserQuestProgressStatusLabel";
import {
  buildQuestIconImageSrc,
  pointKindJaLabel,
  questPointFromFussyPoint,
} from "~/domains/quest/lib";
import {
  fixedHeight,
  fixedWidth,
  flexColumn,
  flexRow,
} from "~/lib/client/design";
import useIsSpMode, { SP_MODE_WIDTH } from "~/lib/client/hooks/useIsSpMode";
import { MeQuestContentQuery } from "~/lib/graphql/client";
import { formatDateTime } from "~/lib/graphql/graphqlClient";

type QuestProgress = MeQuestContentQuery["me"]["userQuestProgresses"][number];

const PcQuestListItem = ({ progress }: { progress: QuestProgress }) => {
  const theme = useTheme();
  const quest = progress.quest;
  const point = questPointFromFussyPoint({
    creatorPoint: quest.creatorPoint,
    curatorPoint: quest.curatorPoint,
    evangelistPoint: quest.evangelistPoint,
    expertPoint: quest.expertPoint,
    supporterPoint: quest.supporterPoint,
  });
  const questIconImageSrc = buildQuestIconImageSrc(quest.category, point);

  return (
    <Link
      href={`/quest/${quest.id}`}
      css={questListItemRootStyle(theme)}
      role={"listitem"}
    >
      <Image
        src={questIconImageSrc}
        alt={quest.title}
        height={120}
        width={120}
        css={iconStyle}
      />
      <div css={questInfoStyle}>
        <div css={questLabelAndStatusStyle}>
          <p css={pointKindStyle}>{pointKindJaLabel(point.kind)}</p>
          <UserQuestProgressStatusLabel status={progress.status} />
        </div>
        <p css={questTitleStyle}>{quest.title}</p>
        <FussyPointLabel point={point} />
        <p css={questDetailUserNameStyle(theme)}>{quest.user.name}</p>
        <p css={questDetailTermStyle}>
          {formatDateTime(quest.startAt)} ~ {formatDateTime(quest.endAt)}
        </p>
      </div>
    </Link>
  );
};

const questListItemRootStyle = (theme: Theme) => css`
  ${fixedWidth("710px")};
  ${fixedHeight("156px")};
  border-radius: 20px;
  padding: 1rem;

  ${flexRow};
  gap: 1.5rem;
  justify-content: flex-start;

  background: ${theme.palette.background.default};

  @media (max-width: ${SP_MODE_WIDTH}px) {
    ${fixedWidth("340px")};
    ${fixedHeight("156px")};
  }
`;

const iconStyle = css`
  width: 120px;
  height: 120px;
`;

const questInfoStyle = css`
  flex: 1;
  ${flexColumn};
  gap: 0.5rem;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    grid-column: 1; /* 1列目に配置 */
    grid-row: 2; /* 2行目に配置 */
  }
`;

const questLabelAndStatusStyle = css`
  ${flexRow};
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    grid-column: 1 / -1; /* 全ての列にまたがる */
    grid-row: 1; /* 1行目に配置 */
  }
`;

const pointKindStyle = css`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
`;

const questTitleStyle = css`
  margin: 0;
  font-size: 15px;
  font-weight: 600;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    ${fixedHeight("88px")};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const questDetailUserNameStyle = (theme: Theme) => css`
  margin: 0;
  font-size: 0.625rem;
  font-weight: 700;
  color: ${theme.palette.secondary.contrastText};
`;

const questDetailTermStyle = css`
  margin: 0;
  font-size: 0.5625rem;
  font-weight: 400;
`;

const SpQuestListItem = ({ progress }: { progress: QuestProgress }) => {
  const theme = useTheme();
  const quest = progress.quest;
  const point = questPointFromFussyPoint({
    creatorPoint: quest.creatorPoint,
    curatorPoint: quest.curatorPoint,
    evangelistPoint: quest.evangelistPoint,
    expertPoint: quest.expertPoint,
    supporterPoint: quest.supporterPoint,
  });
  const questIconImageSrc = buildQuestIconImageSrc(quest.category, point);

  return (
    <Link
      href={`/quest/${quest.id}`}
      role={"listitem"}
      css={spQuestListItemRootStyle(theme)}
    >
      <div css={questLabelAndStatusStyle} className={"labelAndStatus"}>
        <p css={pointKindStyle}>{pointKindJaLabel(point.kind)}</p>
        <UserQuestProgressStatusLabel status={progress.status} />
      </div>
      <div css={questInfoStyle}>
        <p css={questTitleStyle}>{quest.title}</p>
        <FussyPointLabel point={point} />
        <p css={questDetailUserNameStyle(theme)}>{quest.user.name}</p>
        <p css={questDetailTermStyle}>
          {formatDateTime(quest.startAt)} ~ {formatDateTime(quest.endAt)}
        </p>
      </div>
      <div css={questIconContainerStyle}>
        <Image
          src={questIconImageSrc}
          alt={quest.title}
          height={120}
          width={120}
        />
      </div>
    </Link>
  );
};

const spQuestListItemRootStyle = (theme: Theme) => css`
  background: ${theme.palette.background.default};
  display: grid;
  grid-template-columns: 1fr auto 1fr; /* 3列のグリッド、中央の列は内容に応じて自動調整 */
  grid-auto-rows: auto 1fr;
  justify-content: space-between;
  gap: 8px;

  border-radius: 1rem;
  padding: 1rem;

  width: 100%;
  ${fixedHeight("240px")};
`;

const questIconContainerStyle = css`
  grid-column: 3; /* 3列目に配置 */
  grid-row: 2; /* 2行目に配置 */
  border-radius: 20px;

  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const QuestListItem = ({ progress }: { progress: QuestProgress }) => {
  const isSpMode = useIsSpMode();

  if (typeof isSpMode === "undefined") return null;

  return isSpMode ? (
    <SpQuestListItem progress={progress} />
  ) : (
    <PcQuestListItem progress={progress} />
  );
};

export default QuestListItem;
